import React, { useCallback, useEffect, useState } from "react";
import { History, Loader2 } from "lucide-react";
import { fetchLawyerLevelHistory } from "@admin/services/adminApi";

const rupees = (paise) => (paise == null ? null : `₹${Math.round(paise / 100).toLocaleString("en-IN")}`);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function LevelHistory({ lawyerId, refreshKey }) {
  const [state, setState] = useState({ loading: true, entries: [], error: false });
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    fetchLawyerLevelHistory(lawyerId)
      .then((d) => setState({ loading: false, entries: d.entries || [], error: false }))
      .catch(() => setState({ loading: false, entries: [], error: true }));
  }, [lawyerId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="mt-6 border-t border-slate-100 pt-4" data-testid="level-history">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        <History size={12} /> Level history
        {state.loading && <Loader2 size={12} className="animate-spin" />}
      </div>
      {state.error && <p className="mt-2 text-xs text-rose-600" data-testid="level-history-error">Could not load level history.</p>}
      {!state.loading && !state.error && state.entries.length === 0 && (
        <p className="mt-2 text-xs text-slate-500" data-testid="level-history-empty">No level changes recorded yet.</p>
      )}
      {state.entries.length > 0 && (
        <ol className="mt-3 relative border-l border-slate-200 pl-4 space-y-3" data-testid="level-history-list">
          {state.entries.map((e, i) => (
            <li key={`${e.at}-${i}`} className="relative text-sm" data-testid={`level-history-entry-${i}`}>
              <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white ${i === 0 ? "bg-[#D4AF37]" : "bg-slate-300"}`} />
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-extrabold text-[#0A2540]" data-testid={`level-history-level-${i}`}>
                  {e.previous_level != null && e.previous_level !== e.level ? `Level ${e.previous_level} → ` : ""}Level {e.level ?? "?"}
                </span>
                {rupees(e.base_fee_paise) && <span className="text-slate-600">{rupees(e.base_fee_paise)} base fee</span>}
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">
                  {e.source === "application_approve" ? `Lawyer request${e.requested_level && e.requested_level !== e.level ? ` (asked L${e.requested_level})` : ""}` : "Admin assignment"}
                </span>
              </div>
              <div className="text-xs text-slate-500">
                <span data-testid={`level-history-when-${i}`}>{when(e.at)}</span> · by <span data-testid={`level-history-who-${i}`}>{e.actor_email || "—"}</span>
              </div>
              {e.reason && <p className="text-xs text-slate-700 mt-0.5" data-testid={`level-history-why-${i}`}>“{e.reason}”</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
